import React, { useState, useEffect } from 'react';
import { footballAPI } from '../api/api';
import { ExternalLink, ChevronLeft, Clock, Newspaper } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ─── helpers ─────────────────────────────────────────────── */
const SOURCE_COLORS = {
  'beIN SPORTS': '#dc2626', 'بي إن سبورتس': '#dc2626',
  'الجزيرة رياضة': '#7c3aed', 'الجزيرة نت': '#7c3aed',
  'BBC Arabic': '#bb1919', 'BBC عربي': '#bb1919',
  'يلاكورة': '#059669', 'Goal.com': '#d97706',
  'اندبندنت عربية': '#0891b2', 'فرانس 24': '#1d4ed8',
};
const FALLBACK_COLORS = ['#6d28d9','#0891b2','#059669','#d97706','#dc2626','#2563eb'];
function srcColor(src) {
  if (SOURCE_COLORS[src]) return SOURCE_COLORS[src];
  let h = 0;
  for (let i = 0; i < src.length; i++) h = src.charCodeAt(i) + ((h << 5) - h);
  return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length];
}

function timeAgo(pub) {
  if (!pub) return '';
  try {
    const diff = (Date.now() - new Date(pub).getTime()) / 1000;
    if (diff < 3600)  return `منذ ${Math.max(1,Math.floor(diff/60))} د`;
    if (diff < 86400) return `منذ ${Math.floor(diff/3600)} س`;
    return `منذ ${Math.floor(diff/86400)} يوم`;
  } catch { return ''; }
}

/* ─── skeleton ────────────────────────────────────────────── */
function Skeleton({ isDark }) {
  const s = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  return (
    <div className="shrink-0 w-[260px] h-[90px] flex gap-3 items-center px-3 py-3 rounded-2xl"
      style={{ background: isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9', border: `1px solid ${isDark?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.06)'}` }}>
      <div className="w-16 h-16 rounded-xl shrink-0 animate-pulse" style={{ background: s }} />
      <div className="flex-1 space-y-2">
        <div className="h-2.5 w-16 rounded-full animate-pulse" style={{ background: s }} />
        <div className="h-3 w-full rounded-full animate-pulse" style={{ background: s }} />
        <div className="h-3 w-3/4 rounded-full animate-pulse" style={{ background: s }} />
      </div>
    </div>
  );
}

/* ─── main component ──────────────────────────────────────── */
export default function NewsSection({ query, title = 'أحدث الأخبار', isDark = true, fullMode = false }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const fn = query
      ? footballAPI.getNewsSearch(query)
      : footballAPI.getNews();
    fn.then(r => {
      if (!cancelled) {
        setArticles(r.data?.articles?.slice(0, fullMode ? 20 : 8) || []);
        setLoading(false);
      }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query, fullMode]);

  const border  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const cardBg  = isDark ? 'rgba(10,21,38,0.7)'      : '#ffffff';
  const textPri = isDark ? '#ffffff'                  : '#0f172a';
  const textSec = isDark ? 'rgba(255,255,255,0.45)'   : '#64748b';

  return (
    <div className="mt-6" data-testid="news-section">
      {/* header */}
      <div className="flex items-center justify-between px-4 mb-3">
        <div className="flex items-center gap-2">
          <Newspaper className="w-4 h-4" style={{ color: '#FFD028' }} />
          <span className="font-bold text-sm" style={{ color: textPri }}>{title}</span>
        </div>
        <button onClick={() => navigate('/news')}
          className="flex items-center gap-1 text-xs font-bold"
          style={{ color: '#FFD028' }}>
          عرض الكل <ChevronLeft className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* horizontal strip OR full grid */}
      {fullMode ? (
        <div className="px-4 space-y-3">
          {loading
            ? [1,2,3,4,5].map(i => (
                <div key={i} className="flex gap-3 p-3 rounded-2xl animate-pulse"
                  style={{ background: isDark?'rgba(255,255,255,0.03)':'#f1f5f9', border:`1px solid ${border}` }}>
                  <div className="w-20 h-20 rounded-xl shrink-0" style={{ background: isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)' }} />
                  <div className="flex-1 space-y-2 py-1">
                    <div className="h-2.5 w-16 rounded-full" style={{ background: isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)' }} />
                    <div className="h-3.5 w-full rounded-full" style={{ background: isDark?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.07)' }} />
                  </div>
                </div>
              ))
            : articles.length === 0
              ? <p className="text-center py-8 text-xs" style={{ color: textSec }}>لا توجد أخبار</p>
              : articles.map((a, i) => {
                  const color = srcColor(a.source || '');
                  return (
                    <a key={i} href={a.link} target="_blank" rel="noopener noreferrer"
                      data-testid={`news-full-card-${i}`}
                      className="flex gap-3 p-3 rounded-2xl transition-transform active:scale-[0.98]"
                      style={{ background: cardBg, border: `1px solid ${border}`, backdropFilter:'blur(8px)' }}>
                      {a.image && <img src={a.image} alt="" onError={e=>e.target.style.display='none'}
                        className="w-20 h-20 rounded-xl object-cover shrink-0" />}
                      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                        <div>
                          <span className="text-[9px] font-black px-2 py-0.5 rounded-full inline-block mb-1"
                            style={{ background: color, color: '#fff' }}>{a.source}</span>
                          <p className="text-sm font-bold leading-snug line-clamp-2" style={{ color: textPri }}>{a.title}</p>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" style={{ color: textSec }} />
                          <span className="text-[10px]" style={{ color: textSec }}>{timeAgo(a.pub_date)}</span>
                        </div>
                      </div>
                    </a>
                  );
                })
          }
        </div>
      ) : (
      <div className="flex gap-3 overflow-x-auto pb-3 px-4 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {loading
          ? [1,2,3,4].map(i => <Skeleton key={i} isDark={isDark} />)
          : articles.length === 0
            ? (
              <div className="w-full text-center py-6 text-xs" style={{ color: textSec }}>
                لا توجد أخبار حالياً
              </div>
            )
            : articles.map((a, i) => {
              const color = srcColor(a.source || '');
              return (
                <a key={i} href={a.link} target="_blank" rel="noopener noreferrer"
                  data-testid={`news-card-${i}`}
                  className="shrink-0 w-[260px] flex gap-3 items-start px-3 py-3 rounded-2xl snap-start transition-transform hover:scale-[0.98] active:scale-[0.97]"
                  style={{ background: cardBg, border: `1px solid ${border}`, backdropFilter: 'blur(12px)' }}>
                  {a.image && (
                    <img src={a.image} alt="" onError={e => e.target.style.display='none'}
                      className="w-16 h-16 rounded-xl object-cover shrink-0" />
                  )}
                  <div className="flex-1 min-w-0 space-y-1">
                    <span className="text-[9px] font-black px-2 py-0.5 rounded-full inline-block"
                      style={{ background: color, color: '#fff' }}>
                      {a.source}
                    </span>
                    <p className="text-xs font-bold leading-snug line-clamp-2" style={{ color: textPri }}>{a.title}</p>
                    <div className="flex items-center gap-1" style={{ color: textSec }}>
                      <Clock className="w-3 h-3" />
                      <span className="text-[10px]">{timeAgo(a.pub_date)}</span>
                    </div>
                  </div>
                </a>
              );
            })
        }
      </div>
      )}
    </div>
  );
}
